<template>
    <Head :title="title" />

    <div :class="overflowColor">
        <div
            :class="[
                'min-h-screen max-w-[var(--max-site-width)] mx-auto text-text relative',
                borderClass,
            ]"
        >
            <slot />
            <SessionFlashNotification />
            <BusFlashNotification />
            <MatomoConsentPopup v-if="useMatomo" />
        </div>
    </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import SessionFlashNotification from '@/Components/Notifications/FlashNotifications/SessionFlashNotification.vue'
import BusFlashNotification from '@/Components/Notifications/FlashNotifications/BusFlashNotification.vue'
import MatomoConsentPopup from '@/Components/Other/MatomoConsentPopup.vue'
import { router, usePage } from '@inertiajs/vue3'

export default {
    name: 'BaseLayout',

    components: {
        SessionFlashNotification,
        BusFlashNotification,
        Head,
        MatomoConsentPopup,
    },

    // This is used for phosporIcons, it sets the size globally to 100% so that we can control them visa css
    provide: {
        size: null,
        weight: 'regular',
    },

    props: {
        title: {
            type: String,
        },
        overflowColor: {
            type: String,
        },
        borderClass: {
            type: String,
        },
    },

    data() {
        return {
            isTracking: false,
        }
    },

    computed: {
        useMatomo() {
            return this.matomoConfig?.host && this.matomoConfig?.site_id
        },
        cookiebotId() {
            return import.meta.env.VITE_COOKIEBOT_UUID
        },
        hubSpotId() {
            return import.meta.env.VITE_HUBSPOT_ID
        },

        useRekAi() {
            return !!this.rekAiConfig && !!this.rekAiConfig.src
        },

        rekAiConfig() {
            return this?.$page?.props?.rekAi
        },

        matomoConfig() {
            return this?.$page?.props?.matomo
        },
    },

    created() {
        if (this.cookiebotId) {
            this.hasDecidedConsent = Cookiebot.declined || Cookiebot.concented
        } else {
            this.hasDecidedConsent = false
        }

        this.handleFirstTab = (e) => {
            // Tab-key
            if (e.keyCode === 9) {
                document.body.classList.add('user-is-tabbing')
                window.removeEventListener('keydown', this.handleFirstTab)
            }
        }

        if (this.useMatomo) {
            this.setupMatomo()
        }

        if (this.cookiebotId) {
            this.setupCookiebot()
        }

        if (this.hubSpotId) {
            this.setupHubSpot()
        }

        if (this.useRekAi) {
            this.setupRekAi()
        }
    },

    mounted() {
        window.addEventListener('keydown', this.handleFirstTab)
    },

    beforeUnmount() {
        if (this.handleFirstTab) {
            window.removeEventListener('keydown', this.handleFirstTab)
        }
    },

    methods: {
        /**
         * We have to wait for the matomo script to be loaded before we can start tracking
         * Call this.startTracking() when the matomo script is loaded
         */
        setupMatomo() {
            let fetchMatomo = (counter = 0) => {
                if (this.$matomo) {
                    this.startTracking()
                } else if (counter < 5) {
                    setTimeout(() => {
                        fetchMatomo(counter + 1)
                    }, 100)
                }
            }
            fetchMatomo()
        },

        /**
         * When the user accepts or declines cookies, we need to refresh the page
         */
        setupCookiebot() {
            window.addEventListener(
                'CookiebotOnDecline',
                (e) => {
                    if (!this.hasDecidedConsent) {
                        window.location.reload()
                    } else {
                        // Handle consent change
                        this.handleConsentChange()
                    }
                },
                false
            )

            // Also listen for accept events where consent might be changed
            window.addEventListener(
                'CookiebotOnAccept',
                (e) => {
                    this.handleConsentChange()
                },
                false
            )
        },

        handleConsentChange() {
            // Check if HubSpot should be enabled or disabled
            if (this.hubSpotId) {
                if (Cookiebot.consent.statistics) {
                    this.setupHubSpot()
                    this.setupRekAi()
                } else {
                    this.disableHubSpot()
                    this.disableRekAi()
                }
            }
        },

        disableHubSpot() {
            if (this.hubSpotId && import.meta.env.PROD) {
                // TODO, remove cookies and stop tracking if they stopped consenting. Will the below work?
                // window._hsp.push(['revokeCookieConsent'])
                // window._hsq.push(['doNotTrack'])

                // Remove HubSpot script if it exists
                const hsScript = document.getElementById('hs-script-loader')
                if (hsScript) {
                    hsScript.remove()
                }

                // Reset the tracking state
                this.isTracking = false

                // Disable tracking functionality
                if (window._hsq) {
                    // Clear the queue and add a function that does nothing for future calls
                    window._hsq = {
                        push: function () {
                            return null
                        },
                    }
                }
            }
        },

        setupHubSpot() {
            if (!this.cookiebotId || !this.hubSpotId || !import.meta.env.PROD) {
                return
            }

            // Wait for Cookiebot to be ready
            const initHubSpot = () => {
                if (typeof Cookiebot === 'undefined') {
                    setTimeout(initHubSpot, 100)
                    return
                }

                if (Cookiebot.consent.statistics) {
                    this.loadHubSpotScript()
                }
            }

            initHubSpot()
        },

        setupRekAi() {
            if (!this.cookiebotId || !this.useRekAi || !import.meta.env.PROD) {
                return
            }

            const initRekAi = () => {
                if (typeof Cookiebot === 'undefined') {
                    setTimeout(initRekAi, 100)
                    return
                }

                if (Cookiebot.consent.statistics) {
                    this.loadRekAiScript()
                }
            }

            initRekAi()
        },

        disableRekAi() {
            const rekAiScript = document.getElementById('rek-ai-script')
            if (rekAiScript) {
                rekAiScript.remove()
            }
        },

        loadRekAiScript() {
            const script = document.createElement('script')
            script.src = this.rekAiConfig.src
            script.type = 'text/javascript'
            script.defer = true
            script.id = 'rek-ai-script'
            document.head.appendChild(script)
        },

        loadHubSpotScript() {
            const script = document.createElement('script')
            script.src = `https://js-eu1.hs-scripts.com/${this.hubSpotId}.js`
            script.type = 'text/javascript'
            script.async = true
            script.defer = true
            script.id = 'hs-script-loader'
            document.head.appendChild(script)
            setTimeout(() => {
                this.startTracking()
            }, 1000)
        },

        startTracking() {
            if (!this.isTracking) {
                this.isTracking = true
                router.on('navigate', this.onNavigate)
            }
        },

        onNavigate() {
            if (this.$matomo) {
                // we don't have consent
                if (!this.$matomo?.hasRememberedConsent()) {
                    const title = this.isBlackListedRoute()
                        ? 'Redacted - Privacy'
                        : document.title

                    this.$matomo.setDocumentTitle(title)
                    this.$matomo.resetUserId()
                    this.$matomo.disableCookies()
                    this.$matomo.disableBrowserFeatureDetection()
                } else {
                    const user = usePage().props.auth?.user?.id || null

                    this.$matomo.setUserId(user)
                    this.$matomo.setDocumentTitle(document.title)
                    // this.$matomo.enableCookies() does not exist, not sure if it is needed
                    this.$matomo.enableBrowserFeatureDetection()
                }

                this.$matomo.trackPageView()
            }

            if (
                this.hubSpotId &&
                window._hsq &&
                typeof window._hsq.push === 'function' &&
                Cookiebot &&
                Cookiebot.consent &&
                Cookiebot.consent.statistics
            ) {
                window._hsq.push(['trackPageView'])
            }
        },

        isBlackListedRoute() {
            return route().current('*user.show')
        },
    },
}
</script>
